import { FC, Fragment, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ImageViewer from 'react-simple-image-viewer';
import {
  Alert,
  Dialog,
  Grid,
  GridProps,
  Paper,
  Stack,
  StackProps,
  styled,
  Typography,
  TypographyVariant,
} from '@mui/material';

import { Icon, IconButton, Loading } from '@components/base';
import useImageGroupData from '@hooks/imageHooks/useImageGroupData';
import { ImageContext, ImageMetadataMap } from '@hooks/imageHooks/useImageGroupData/context';
import { ImageHeaders } from '@hooks/imageHooks/useImageHeaders';
import { ImageType, ImageTypes } from '@models/Image';

import ImageDisplay, { ImageDisplayProps } from './ImageDisplay';

export type ImageGroupDisplayProps = Pick<StackProps, 'spacing'> &
  Pick<ImageDisplayProps, 'ImageCardProps' | 'minified'> & {
    ContainerProps?: Omit<GridProps, 'container' | 'item'>;
    disableClickable?: boolean;
    /**
     * Image display should be grouped if there are multiple sets of images, i.e. collection scoped title images and default title images
     */
    displayMode?: 'flat' | 'grouped';
    headingVariant?: TypographyVariant;
    hideFallback?: boolean;
    /**
     * Hide images that were not found from the display
     */
    hideMissing?: boolean;
    /**
     * Base preview width applied to square images and calculated for other ratios
     */
    previewWidth?: number;
    /**
     * When the value of the trigger is updated, all images are fetched again
     */
    trigger?: number;
  };

const ImageViewerPaperComponent = styled(Paper)`
  .react-simple-image-viewer {
    &__close {
      top: 0;
    }

    &__close,
    &__next,
    &__previous {
      opacity: 1;
    }
  }
`;

type ImageGroupProps = Omit<ImageGroupDisplayProps, 'displayMode'> & {
  context?: ImageContext;
  imageMap: ImageMetadataMap;
};

const ImageGroup: FC<ImageGroupProps> = ({
  ContainerProps: containerProps = {},
  context,
  disableClickable = false,
  hideFallback,
  hideMissing,
  ImageCardProps: imageCardProps,
  imageMap,
  minified,
  spacing = 2,
}) => {
  const { t } = useTranslation();

  const { disabledImageTypes, loading, defaultImageMissing, defaultImageType, deleteImage } =
    useImageGroupData();

  const [imageViewerIndex, setImageViewerIndex] = useState<number>();
  const toggleImageViewer = (index?: number) => setImageViewerIndex(index);

  const filtered = ImageTypes.reduce((acc: Partial<Record<ImageType, ImageHeaders>>, imageType) => {
    if (disabledImageTypes.includes(imageType)) return acc;

    const imageMetadata = imageMap[imageType];

    if (!imageMetadata) return acc;
    if (hideMissing && !imageMetadata.wasFound) return acc;
    if (hideFallback && imageMetadata.isFallback) return acc;

    return { ...acc, [imageType]: imageMetadata };
  }, {});

  const displayWarning = (!context || context === 'uploaded') && defaultImageMissing;

  return (
    <Fragment>
      {hideMissing && Object.entries(filtered).length === 0 && !loading ? (
        <Alert severity='info' variant='standard'>
          {t('images.uploaded.empty')}
        </Alert>
      ) : Object.entries(filtered).length > 0 ? (
        <Stack spacing={spacing}>
          {displayWarning && (
            <Alert severity='warning'>
              <Trans
                components={[<strong />]}
                i18nKey='images.default.missing'
                values={{ imageType: defaultImageType, context: '' }}
              />
            </Alert>
          )}

          <Grid container spacing={spacing} {...containerProps}>
            {Object.entries(filtered).map(([imageType, imageMetadata], index) => (
              <ImageDisplay
                context={context}
                deleteImage={deleteImage ? () => deleteImage(imageType as ImageType) : undefined}
                ImageCardProps={imageCardProps}
                imageMetadata={imageMetadata}
                imageType={imageType as ImageType}
                key={imageType}
                minified={minified}
                onClick={disableClickable ? undefined : () => toggleImageViewer(index)}
              />
            ))}
          </Grid>
        </Stack>
      ) : loading ? (
        <Loading BoxProps={{ p: 4 }} />
      ) : null}

      {imageViewerIndex != null && (
        <Dialog
          fullScreen
          onClose={() => toggleImageViewer()}
          open
          PaperComponent={ImageViewerPaperComponent}
        >
          <ImageViewer
            backgroundStyle={{ zIndex: 2, marginTop: 0 }}
            closeComponent={<IconButton icon='close' size='medium' />}
            closeOnClickOutside
            currentIndex={imageViewerIndex}
            leftArrowComponent={<Icon fontSize='inherit' name='chevronLeft' />}
            onClose={toggleImageViewer}
            rightArrowComponent={<Icon fontSize='inherit' name='chevronRight' />}
            src={Object.values(filtered).map(({ getSrc }) => getSrc()) as string[]}
          />
        </Dialog>
      )}
    </Fragment>
  );
};

const ImageGroupDisplay: FC<ImageGroupDisplayProps> = ({
  displayMode = 'flat',
  headingVariant = 'h3',
  ...rest
}) => {
  const { t } = useTranslation();
  const { imageMap, defaultImages, contextImages } = useImageGroupData();

  return displayMode === 'grouped' ? (
    <Fragment>
      <Typography mb={2} variant={headingVariant}>
        {t('images.uploaded')}
      </Typography>

      <ImageGroup context='uploaded' hideMissing imageMap={contextImages} {...rest} />

      <Typography mb={2} mt={3} variant={headingVariant}>
        {t('images.default')}
      </Typography>

      <ImageGroup context='default' imageMap={defaultImages} {...rest} />
    </Fragment>
  ) : (
    <ImageGroup imageMap={imageMap} {...rest} />
  );
};

export default ImageGroupDisplay;
